import queryString from 'query-string';
import jwtDecode from 'jwt-decode';
import { getJSON } from 'redux-api-middleware';
import fp from 'lodash/fp';

/**
 * Reduces an array of messages from the server to a single message.
 * @param {String[]} messages 
 * @param {String} level 
 */
export const reduceMessages = (messages, levels) => (levels ? messages.filter((x) => fp.includes(x.level)(levels)) : messages).map((x) => x.message).join(' ');

/**
 * Default payload converter for api actions
 * @param {Function} payloadCreator 
 */
export const defaultPayloadConverter = (payloadCreator) => payloadCreator;

/**
 * Json payload converter for api middleware actions
 * @param {Function} payloadCreator 
 */
export const jsonPayloadConverter = (payloadCreator) => (action, state, response) => getJSON(response).then((json) => json.result !== undefined ? json.result : json).then(payloadCreator);

/**
 * Json error payload converter for api middleware actions
 * @param {Function} payloadCreator 
 */
export const jsonErrorPayloadConverter = (payloadCreator) => (action, state, response) => getJSON(response).then((json) => reduceMessages(json.messages || json, ['Error', 'Alert'])).then(payloadCreator);

/**
 * Meta data converter for api middleware actions
 * @param {Function} metaCreator 
 */
export const apiMetaConverter = (metaCreator) => (action, state, response) => Object.assign({}, metaCreator(response) || {}, { action, response });

/**
 * Convert api action to standard action
 * @param {Object} action 
 * @param {*} payload 
 */
export const convertApiAction = (action, payload) => Object.assign({}, action, { payload: action.payload(payload) });

/**
 * Payload creator for creating api actions with redux-actions
 * @param {Function} payloadCreator 
 */
export const apiActionPayloadCreator = (payloadCreator) => (payloadConverter) => (payloadConverter || defaultPayloadConverter)(payloadCreator);

/**
 * Meta data creator for creating api actions with redux-actions
 * @param {Function} metaCreator 
 */
export const apiActionMetaCreator = (metaCreator) => () => apiMetaConverter(metaCreator || (() => undefined));

/**
 * Standard action map for creating api actions with redux-actions
 * @param {Function} payloadCreator 
 * @param {Function} metaCreator 
 */
export const apiActionTypeCreator = (payloadCreator, metaCreator) => [apiActionPayloadCreator(payloadCreator), apiActionMetaCreator(metaCreator)]

/**
 * Action creator for a simple resource api action
 * @param {String} resourceName 
 */
export const apiResourceActionCreator = (resourceName) => ({
  REQUEST: undefined,
  SUCCESS: apiActionTypeCreator((resource) => ({ [resourceName]: resource })),
  FAILED: apiActionTypeCreator((error) => ({ error }))
});

/**
 * Get the uri encoded user id from given auth token.
 * @param {String} authToken
 * @return {String} URI encoded User ID, or null if failed to decode the authentication token. 
 */
export const getAuthTokenUserId = (authToken) => {
  try {
    return encodeURI(jwtDecode(authToken).sub);
  }
  catch (e) {
    console.error(`Failed get User ID - [${e.message}].`);
    return null;
  }
}

/*
 * This function does not handle "Date" objects correctly, or at least in a way that our backend can understand.
 *
 * There is a hack for existing code in 'date-fns-compat.js', but going forward this function is best avoided.
 * 
 * All it does is URL encode a generic object. It should probably be replaced by something that allows for custom handlers
 * for particular object types (such as dates).
 */
export const endpointQuery = (query) => query ? `?${queryString.stringify(query)}` : '';

/**
 * log variable values when testing
 */
export const log = (message) => (value) => console.log(message + value) || value;



// WEBPACK FOOTER //
// ./src/state/modules/common/utils.js